import './GenerateQuestion.scss';

import * as React from 'react';
import { useState } from 'react';

import { SlSkeleton } from '@shoelace-style/shoelace/dist/react';
import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { normalize } from 'normalizr';
import { useDispatch } from 'react-redux';

import { BulkAddQuestionsEventAdapter } from '~analytics/adapters/BulkAddQuestionsEventAdapter';
import { QuestionSavedEventAdapter } from '~analytics/adapters/QuestionSavedEventAdapter';
import { createSetAtom } from '~shared/atoms/utils/createSetAtom';
import { Badge } from '~shared/components/Badge';
import {
  IconButton,
  SmallButton,
  SmallLinkButton,
} from '~shared/components/buttons';
import { IconChevron } from '~shared/components/icons/large';
import { IconExternal } from '~shared/components/icons/standard';
import { toast } from '~shared/components/Toast';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { Error as ErrorIcon } from '~teacher/components/ExportReports/ExportFeedback/ExportFeedbackIcons';
import { useBulkQuestionsUrl } from '~teacher/components/QuizEditor/contexts/BulkQuestionsUrlProvider';
import { useQuizContext } from '~teacher/components/QuizEditor/contexts/QuizContextProvider';
import { useQuizQuestionUrl } from '~teacher/components/QuizEditor/contexts/QuizQuestionUrlProvider';
import { useAccount } from '~teacher/hook/account';
import { useBulkCreateQuestions } from '~teacher/hook/question/useBulkCreateQuestions';
import { useCreateQuestion } from '~teacher/hook/question/useCreateQuestion';
import { QuizQuestionSchema } from '~teacher/models/quizzes';
import {
  addQuestionToQuiz,
  changeStatusToOk,
  changeStatusToSaving,
  setQuizAnswers,
  setQuizQuestions,
  updateQuizQuestionOrder,
} from '~teacher/slices/quiz/quizSlice';

import { generateQuestionAtom, positionAtom, useScopedStepAtom } from './atom';
import { useQuestionGenerator } from './context';
import { ExpandableQuestionCard } from './ExpandableQuestionCard';
import { LearnMoreIcon } from './LearnMoreIcon';

export const addedQuestionSetAtom = createSetAtom<string>(new Set<string>());

interface UseAddAllQuestionsParams {
  quizId: number;
  onSuccess: () => void;
  onError: () => void;
}

function useAddAllQuestions({
  quizId,
  onSuccess,
  onError,
}: UseAddAllQuestionsParams) {
  const questionGenerator = useQuestionGenerator();
  const bulkQuestionsUrl = useBulkQuestionsUrl();
  const bulkCreateQuestions = useBulkCreateQuestions({ url: bulkQuestionsUrl });

  const [position] = useAtom(positionAtom);
  const addedQuestionSet = useAtomValue(addedQuestionSetAtom.valueAtom);
  const addToAddedQuestionSet = useSetAtom(addedQuestionSetAtom.addAtom);
  const removeFromAddedQuestionSet = useSetAtom(
    addedQuestionSetAtom.removeAtom
  );

  const [isAddingAllQuestions, setIsAddingAllQuestions] = useState(false);

  if (!questionGenerator.data) {
    return { addAllQuestions: () => {}, isAddingAllQuestions: false };
  }

  const numQuestionsToAddOfType = new Map<QuestionTypes, number>([
    [QuestionTypes.MultipleChoice, 0],
    [QuestionTypes.TrueFalse, 0],
    [QuestionTypes.FreeResponse, 0],
  ]);
  const questionsToAdd = questionGenerator.data.questions.filter(
    ({ questionId }) => !addedQuestionSet.has(questionId)
  );

  return {
    isAddingAllQuestions: isAddingAllQuestions,
    addAllQuestions: () => {
      setIsAddingAllQuestions(true);
      questionsToAdd.forEach(({ questionId }) => {
        addToAddedQuestionSet(questionId);
      });

      bulkCreateQuestions.mutate(
        {
          insertionOrder: position,
          quizId: quizId as any,
          questions: questionsToAdd.map(
            ({ questionType, questionText, answers, explanation }) => {
              numQuestionsToAddOfType.set(
                questionType,
                numQuestionsToAddOfType.get(questionType) + 1
              );
              return {
                type: questionType,
                questionText: questionText,
                answers: answers,
                explanation: explanation,
                gradingWeight: 1,
              };
            }
          ),
        },
        {
          onSuccess: () => {
            // Analytics
            new BulkAddQuestionsEventAdapter({
              numQuestions: questionsToAdd.length,
              numMultipleChoiceQuestions: numQuestionsToAddOfType.get(
                QuestionTypes.MultipleChoice
              ),
              numTrueFalseQuestions: numQuestionsToAddOfType.get(
                QuestionTypes.TrueFalse
              ),
              numShortAnswerQuestions: numQuestionsToAddOfType.get(
                QuestionTypes.FreeResponse
              ),
              uiSource: 'ai_import',
            }).sendAnalytics();

            onSuccess();
          },
          onError: () => {
            toast.critical('Something went wrong');
            questionsToAdd.forEach(({ questionId }) => {
              removeFromAddedQuestionSet(questionId);
            });
            setIsAddingAllQuestions(false);
            onError();
          },
        }
      );
    },
  };
}

interface Props {
  onClose: () => void;
  refreshQuestions: () => Promise<void>;
}

export function GenerateQuestionStep2({ onClose, refreshQuestions }: Props) {
  const [, setStep] = useScopedStepAtom();
  const questionGenerator = useQuestionGenerator();
  const quizContext = useQuizContext();
  const appContext = useTeacherApplicationContext();

  const { addAllQuestions, isAddingAllQuestions } = useAddAllQuestions({
    quizId: quizContext.quizId,
    onSuccess: async () => {
      await refreshQuestions();
      onClose();
    },
    onError: () => refreshQuestions(),
  });

  return (
    <>
      <div className="pb-4">
        <div className="flex gap-4">
          <div className="flex flex-col">
            <IconButton
              aria-label={translate('Back')}
              onClick={() => setStep({ type: 'PreviousStep' })}
            >
              <IconChevron direction="left" />
            </IconButton>
          </div>
          <div className="flex flex-shrink flex-col">
            <div className="flex">
              <a
                href="https://help.socrative.com/en/articles/8732134-socrative-ai-question-generation"
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <Badge textTransform="capitalize" tone="ai">
                  {translate('AI Assisted')}
                  <LearnMoreIcon />
                </Badge>
              </a>
            </div>
            <div className="font-display text-md leading-8 text-blue-900 font-700">
              {translate('Generate Questions')}
            </div>
            <div className="text-sans text-base leading-8 text-blue-600">
              {translate(
                'Add questions to your quiz. When you are done, edit the questions on your quiz.'
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="flex flex-col h-full justify-between min-h-0"
        style={{
          boxShadow: '0px 1px 0px 0px #E7EDF0 inset',
        }}
      >
        <GenerateQuestionStep2Content />
        <div
          style={{
            boxShadow: '0px 1px 0px 0px #E7EDF0 inset',
          }}
          className="flex justify-between items-center gap-3 pt-5"
        >
          {appContext === TeacherApplicationContext.Socrative && (
            <AiFeedbackFormLink />
          )}
          <div className="flex gap-3 ml-auto">
            <SmallButton
              isDisabled={isAddingAllQuestions || !questionGenerator.isSuccess}
              appearance="ghost"
              onClick={addAllQuestions}
            >
              {translate('Add All Questions')}
            </SmallButton>
            <SmallButton appearance="primary" onClick={onClose}>
              {translate('Done')}
            </SmallButton>
          </div>
        </div>
      </div>
    </>
  );
}

function AiFeedbackFormLink() {
  const accountQuery = useAccount();
  const account = accountQuery.data;
  const AI_FEEDBACK_FORM_URL = new URL(
    'https://showbie.typeform.com/to/IkH41IWD#' +
      new URLSearchParams({
        email: account?.email,
        name: account?.firstName,
        user_id: account?.publicId,
      }).toString()
  );

  return (
    <SmallLinkButton
      href={AI_FEEDBACK_FORM_URL.href}
      target="_blank"
      appearance="text"
    >
      {translate('Help Us Improve')}
      <IconExternal
        className="text-cyan-700"
        title={translate('External Link')}
        titleId="help-us-improve-external-link"
      />
    </SmallLinkButton>
  );
}

function GenerateQuestionStep2Loading() {
  const [{ questionCount }] = useAtom(generateQuestionAtom);

  return (
    <div className="question-skeletons overflow-y-scroll">
      {[...Array(questionCount)].map((_, index) => (
        <SlSkeleton effect={'pulse'} key={'question-skeleton-' + index} />
      ))}
    </div>
  );
}

function GenerateQuestionStep2Success({
  data,
  quizId,
}: {
  quizId: number;
  data: ReturnType<typeof useQuestionGenerator>['data'];
}) {
  const { questions } = data;
  const [{ questionCount: requestedQuestionCount }] =
    useAtom(generateQuestionAtom);

  const didNotGenerateEnoughQuestions =
    questions.length < requestedQuestionCount;

  return (
    <div className="flex flex-col min-h-0 flex-shrink ">
      {/* Added `overflow-x-hidden` to hide scrollbars in chrome */}
      <div className="overflow-y-scroll overflow-x-hidden min-h-0 flex-shrink">
        {questions.map((question, index) => (
          <GenerateQuestionStep2Question
            key={index}
            quizId={quizId}
            question={question}
          />
        ))}
        {didNotGenerateEnoughQuestions ? (
          <p className="text-center text-sans text-xs leading-6 my-8">
            {translate('Some questions failed to generate. Please try again.')}
          </p>
        ) : null}
      </div>
      <div
        className="text-center text-sans text-xs text-blue-600 leading-6 py-8"
        style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
      >
        {translate(
          'Generated questions can make mistakes. Consider checking question accuracy.'
        )}
      </div>
    </div>
  );
}

interface UseAddQuestionParams {
  quizId: number;
  position: number;
}

function useAddQuestion({ quizId, position }: UseAddQuestionParams) {
  const [, addToAdded] = useAtom(addedQuestionSetAtom.addAtom);
  const quizQuestionUrl = useQuizQuestionUrl();
  const createQuestion = useCreateQuestion();
  const dispatch = useDispatch();

  const addQuestion = async ({
    questionType,
    questionText,
    answers,
    questionId,
    explanation,
  }: {
    questionId: string;
    questionType: QuestionTypes;
    questionText: string;
    answers: { text: string; isCorrect: boolean; order: number }[];
    explanation: string;
  }) => {
    addToAdded(questionId);
    dispatch(changeStatusToSaving());

    const apiResponse = await createQuestion.mutateAsync({
      questionText: questionText,
      quizId: quizId,
      type: questionType,
      explanation: explanation,
      order: position,
      quizQuestionUrl: quizQuestionUrl,
      answers: answers.map((answer) => ({
        questionId: null,
        id: null,
        createdById: null,
        imageUrl: null,
        image: null,
        resources: null,
        text: answer.text,
        isCorrect: answer.isCorrect,
        order: answer.order,
      })),
    });
    const { question, orders } = apiResponse;
    const result_4 = normalize(question, QuizQuestionSchema);
    const {
      entities: { quizQuestion: newQuestions, quizAnswer: newAnswers },
    } = result_4;
    // Save the questions
    dispatch(setQuizQuestions({ questions: newQuestions }));
    // Save the answers
    Object.values(newQuestions).forEach((quizQuestion) => {
      const { answers: answers_1, type } = quizQuestion;
      const answersToSet = answers_1.reduce(
        (aggregator: any, answerId: any) => {
          aggregator[answerId] = newAnswers[answerId];
          return aggregator;
        },
        {}
      );
      dispatch(
        setQuizAnswers({
          answers: answersToSet,
          questionType: type,
        })
      );
    });
    // Add references to questions in quiz
    dispatch(
      addQuestionToQuiz({
        quizId: quizId,
        questionId: question.questionId,
      })
    );
    // Make sure the question order is proper
    dispatch(updateQuizQuestionOrder({ questionOrderMap: orders }));
    // We did okay!
    dispatch(changeStatusToOk());
    // Analytics
    new QuestionSavedEventAdapter({
      originalQuestion: null,
      originalAnswers: null,
      modifiedQuestion: question,
      modifiedAnswers: Object.values(newAnswers ?? {}),
      generationSource: 'AI',
    }).sendAnalytics();
  };
  return { addQuestion };
}

function GenerateQuestionStep2Question({
  question,
  quizId,
}: {
  question: ReturnType<
    typeof useQuestionGenerator
  >['data']['questions'][number];
  quizId: number;
}) {
  const [position] = useAtom(positionAtom);

  const { addQuestion } = useAddQuestion({
    quizId: quizId,
    position: position,
  });

  return (
    <ExpandableQuestionCard
      question={question}
      addQuestion={() =>
        addQuestion({
          questionType: question.questionType,
          questionText: question.questionText,
          answers: question.answers,
          explanation: question.explanation,
          questionId: question.questionId,
        })
      }
    />
  );
}

function GenerateQuestionStep2Content() {
  const questionGenerator = useQuestionGenerator();
  const quizContext = useQuizContext();

  if (questionGenerator.data) {
    return (
      <GenerateQuestionStep2Success
        quizId={quizContext.quizId}
        data={questionGenerator.data}
      />
    );
  }
  if (questionGenerator.isError) {
    return <GenerateQuestionStep2Error />;
  }

  return <GenerateQuestionStep2Loading />;
}

function GenerateQuestionStep2Error() {
  return (
    <div
      data-testid="question-generation-error"
      className="h-80 flex flex-col items-center justify-center w-full p-6"
    >
      <ErrorIcon />
      <span className="pt-4 font-display text-md font-500 text-blue-900">
        {translate('Something Went Wrong')}
      </span>
      <span className="max-w-2xl pt-3 font-sans text-base leading-8 text-blue-900 text-center">
        {translate('An unknown error occurred. Please try again later.')}
      </span>
    </div>
  );
}
